import { useState } from 'react';
import { Form, useNotify, useTranslate } from 'react-admin';
import { DialogActions, DialogContent, DialogTitle, Button as MUIButton } from '@mui/material';
import { ICONS } from 'src/layouts/configs/icons';
import { PermissionButton } from 'src/routes/components/permission-button';
import TransferRequestForm, { TransferFormState } from './SignTransactionForm';
import useSignTransaction from './useSignTransaction';

/** "Request transfer" dialog: creates a PENDING request that another admin must approve. */
const SignTransaction = ({
  chain,
  address,
  nativeBalance,
  usdcBalance,
  onClose,
}: {
  chain: string;
  address: string;
  nativeBalance: string;
  usdcBalance: string;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const { requestTransfer } = useSignTransaction();
  const notify = useNotify();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<TransferFormState>({
    asset: '',
    toAddress: '',
    amount: '',
    reason: '',
    isValid: false,
  });

  const handleSubmit = async () => {
    if (!formData.asset) return;
    setSaving(true);
    await requestTransfer(
      {
        chain,
        asset: formData.asset,
        fromAddress: address,
        toAddress: formData.toAddress,
        amount: formData.amount,
        reason: formData.reason.trim() || undefined,
      },
      {
        onSuccess: () => {
          notify(
            translate('module.financeTransfers.requested', {
              _: 'Transfer requested. Another admin must approve it under Transfer requests.',
            }),
            { type: 'success' }
          );
          onClose();
        },
        onError: (error) => {
          // Shows the API code and message, for example DESTINATION_NOT_ALLOWLISTED.
          notify(error.message || 'UNKNOWN_ERROR', { type: 'error' });
        },
      }
    );
    setSaving(false);
  };

  return (
    <Form disableInvalidFormNotification>
      <DialogTitle>{translate('resource.requestTransfer', { _: 'Request transfer' })}</DialogTitle>
      <DialogContent>
        <TransferRequestForm
          chain={chain}
          address={address}
          nativeBalance={nativeBalance}
          usdcBalance={usdcBalance}
          onChange={setFormData}
        />
      </DialogContent>
      <DialogActions>
        <MUIButton variant="outlined" onClick={onClose}>
          {translate('actions.cancel')}
        </MUIButton>
        <PermissionButton resource="finance-transfer" action="request">
          <MUIButton
            variant="outlined"
            disabled={!formData.isValid || saving}
            onClick={handleSubmit}
            startIcon={ICONS.pen}
          >
            {translate('resource.requestTransfer', { _: 'Request transfer' })}
          </MUIButton>
        </PermissionButton>
      </DialogActions>
    </Form>
  );
};

export default SignTransaction;
